import { DOSSIERS_OPEN_SEARCH_SLICE_NAME, useFormaliteService } from '@europrocurement/l2d-domain';
import { type DossierOpenSearchJsonldOpensearchDossierStatutRead } from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { useSnackbar } from 'notistack';
import React, { useEffect, useMemo, useState } from 'react';
import { useTheme } from '@mui/material';
import { Accordion } from '@europrocurement/flexy-components';
import { dateToISOYmd } from '@europrocurement/l2d-utils';
import { type NavigateFunction } from 'react-router';
import { useDispatch } from 'react-redux';
import { DOSSIER_STATUS_ARCHIVED } from '@europrocurement/l2d-modules/modules/Dossiers';
import FiltersDashboard from '../../components/FiltersDashboard/FiltersDashboard';
import DossierStatusWidgetDashboard from '../../components/DossierStatusWidgetDashboard/DossierStatusWidgetDashboard';
import TasksAndChartWidgetDashboard from '../../components/TasksAndChartWidgetDashboard/TasksAndChartWidgetDashboard';
import GreffesStatusWidgetDashboard from '../../components/GreffesStatusWidgetDashboard/GreffesStatusWidgetDashboard';

type FormalistDashboardProps = {
    title: string;
    formalistIds: number[];
    listeDossiersPath: string;
    navigate: NavigateFunction;
    formalistName?: string;
};

export type DashboardFiltersType = {
    startDate: string;
    endDate?: string;
};

const FormalistDashboard: React.FunctionComponent<FormalistDashboardProps> = function ({
    title,
    formalistIds,
    listeDossiersPath,
    navigate,
    formalistName,
}) {
    const { palette } = useTheme();
    const { dossierOpenSearchApi } = useFormaliteService();
    const { enqueueSnackbar } = useSnackbar();
    const dispatch = useDispatch();

    const [statusDossierOpenSearch, setStatusDossierOpenSearch] =
        useState<DossierOpenSearchJsonldOpensearchDossierStatutRead[]>();
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    // Filters
    const initialStartDate = useMemo(() => {
        const DAYS_OFFSET = 30;
        const date = new Date();
        date.setDate(date.getDate() - DAYS_OFFSET);
        return dateToISOYmd(date);
    }, []);

    const [filters, setFilters] = useState<DashboardFiltersType>({
        startDate: initialStartDate,
        endDate: undefined,
    });

    const handleDossierStatusClick = (statusId?: number | null) => {
        if (!statusId) {
            return;
        }
        dispatch({ type: `${DOSSIERS_OPEN_SEARCH_SLICE_NAME}/resetmainFilter` });
        // dispatch  dossier status to redux and open the dossier list
        dispatch({
            type: `${DOSSIERS_OPEN_SEARCH_SLICE_NAME}/setmainFilter`,
            payload: { key: 'formalisteId', value: formalistIds },
        });
        dispatch({
            type: `${DOSSIERS_OPEN_SEARCH_SLICE_NAME}/setmainFilter`,
            payload: { key: 'dateCreation', value: filters },
        });

        // Displayed label on formalist filter
        if (formalistName) {
            dispatch({
                type: `${DOSSIERS_OPEN_SEARCH_SLICE_NAME}/setmainFilterLabel`,
                payload: { key: 'formalisteId', label: formalistName },
            });
        }

        dispatch({
            type: `${DOSSIERS_OPEN_SEARCH_SLICE_NAME}/setmainPreFilter`,
            payload: {
                filters: [
                    {
                        field: 'statutId',
                        value: statusId,
                    },
                ],
            },
        });

        navigate(`/${listeDossiersPath}`);
    };

    const fetchDossierStatus = async () => {
        setIsSubmitting(true);
        const dossierStatusResponse = await dossierOpenSearchApi.apiOpensearchStatusesGetCollection(
            {
                formalisteId: formalistIds,
                itemsPerPage: 20,
                startDate: filters.startDate,
                endDate: filters.endDate,
            },
        );

        setIsSubmitting(false);
        if (dossierStatusResponse.status === 200) {
            return dossierStatusResponse.data['hydra:member'];
        }
        enqueueSnackbar('Une erreur est survenue lors de la récupération des dossiers', {
            variant: 'error',
        });
        return [];
    };

    // refetch dossierStatus on filter or formalistId update
    useEffect(() => {
        fetchDossierStatus().then((res) =>
            setStatusDossierOpenSearch(
                res.filter(
                    (statutDossier) =>
                        ![DOSSIER_STATUS_ARCHIVED].includes(statutDossier.id as number),
                ),
            ),
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formalistIds, filters.startDate, filters.endDate]);

    return (
        <Accordion
            title={title}
            expandLabel={
                <FiltersDashboard
                    isSubmitting={isSubmitting}
                    setFilters={setFilters}
                    initialStartDate={initialStartDate}
                />
            }
            expanded
            expandColor={palette.primary}
            sx={{ boxShadow: 'unset', border: 'none' }}
            contentSxProps={{ gap: '16px', display: 'grid' }}
        >
            {statusDossierOpenSearch && (
                <DossierStatusWidgetDashboard
                    statusDossierOpenSearch={statusDossierOpenSearch}
                    handleDossierStatusClick={handleDossierStatusClick}
                />
            )}

            <GreffesStatusWidgetDashboard
                formalistIds={formalistIds}
                filters={filters}
                listeDossiersPath={listeDossiersPath}
                localStorageName="grid-status-greffes-formalist"
                formalistName={formalistName}
            />
            <TasksAndChartWidgetDashboard
                statusDossierOpenSearch={statusDossierOpenSearch}
                formalistIds={formalistIds}
            />
        </Accordion>
    );
};

export default FormalistDashboard;
